import PropTypes from "prop-types";
import cn from "classnames";

import { NextHead } from "@components/nextHead";
import { ConditionalWrapper } from "@utils/conditionalWrapper";
import {
  ChevronRight,
  WorldPinFeature,
  StoriesFeature,
  ViewFeature,
} from "@icons";

export const breadcrumbs = url =>
  url
    .replace(/^\/+/, "")
    .split("/")
    .reduce((acc, segment, i) => {
      const label = segment.split("?")[0].split("-").join(" ");

      const obj = {
        label,
        path: i === 0 ? `/${segment}` : `${acc[i - 1].path}/${segment}`,
      };

      acc.push(obj);
      return acc;
    }, []);

const sitemaps = [
  { name: "Articles", href: "/sitemaps/articles", icon: StoriesFeature },
  {
    name: "Destinations",
    href: "/sitemaps/destinations",
    icon: WorldPinFeature,
  },
  { name: "News", href: "/sitemaps/news", icon: ViewFeature },
];

export default function SitemapPage({ children, metadata }) {
  return (
    <div className="container mb-12">
      <NextHead>
        <title>{`${metadata.title} - Lonely Planet`}</title>
        <meta
          key="title"
          name="title"
          content={`${metadata.title} - Lonely Planet`}
        />

        <link
          key="canonical"
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_DOTCOM_FRONTEND_URL}${metadata.breadcrumbs[metadata.breadcrumbs.length - 1]?.path || ""}`}
        />
      </NextHead>

      {/* BREADCRUMBS */}
      <nav className="py-12">
        <ol className="flex gap-2 font-semibold">
          <li className="flex items-center gap-1">
            <a href="/" className="text-black">
              Home
            </a>
            <span>
              <ChevronRight className="inline text-tiny text-slate" />
            </span>
          </li>

          {metadata.breadcrumbs.map((crumb, i) => {
            const isLast = i + 1 === metadata.breadcrumbs.length;

            return (
              <li className="flex items-center gap-1" key={crumb.path}>
                <ConditionalWrapper
                  condition={!isLast}
                  // eslint-disable-next-line react/no-unstable-nested-components
                  wrapper={c => (
                    <a href={crumb.path} className="no-underline">
                      {c}
                    </a>
                  )}
                >
                  <span
                    className={cn(
                      isLast ? "text-blue" : "text-black",
                      "capitalize"
                    )}
                  >
                    {crumb.label}
                  </span>
                </ConditionalWrapper>

                {!isLast && (
                  <span>
                    <ChevronRight className="inline text-tiny text-slate" />
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="grid grid-cols-5 gap-8">
        {/**
         * Only show the sidebar on non-index pages
         */}
        {!metadata.index && (
          <aside className="col-span-1">
            <nav className="sticky top-8">
              <ul className="flex flex-col gap-2">
                {["articles", "news", "destinations"].map(item => {
                  const active = metadata.breadcrumbs.some(breadcrumb =>
                    breadcrumb.path.includes(item)
                  );

                  return (
                    <li key={item}>
                      <a
                        href={`/sitemaps/${item}`}
                        className={cn(
                          "inline-block w-1/2 rounded-sm px-3 py-2 text-sm font-semibold capitalize",
                          active
                            ? "bg-blue-100 text-blue"
                            : "hover:bg-black-100 text-black-400"
                        )}
                      >
                        {item}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>
        )}

        {/**
         * Adjust the content width to account for presence of sidebar
         */}
        <section className={cn(metadata.index ? "col-span-5" : "col-span-4")}>
          {!metadata.index && (
            <h1 className="text-3xl font-display font-semibold">
              {metadata.title}
            </h1>
          )}

          {metadata.index && (
            <div className="grid grid-cols-3 gap-2 lg:gap-6">
              {sitemaps.map(sm => {
                const Icon = sm.icon;

                return (
                  <article key={sm.href}>
                    <a
                      href={sm.href}
                      className="bg-black-100 rounded p-2 aspect-square flex flex-col justify-center items-center text-slate hover:text-black"
                    >
                      <Icon className="block text-xl lg:text-4xl mb-1 lg:mb-2" />
                      <span className="text-sm lg:text-xl font-semibold font-display">
                        {sm.name}
                      </span>
                    </a>
                  </article>
                );
              })}
            </div>
          )}

          <nav className="mt-2">{children}</nav>
        </section>
      </div>
    </div>
  );
}

export async function getServerSideProps({ resolvedUrl }) {
  return {
    props: {
      metadata: {
        title: "Sitemaps",
        breadcrumbs: breadcrumbs(resolvedUrl),
        index: true,
      },
    },
  };
}

SitemapPage.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  metadata: PropTypes.shape({
    title: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired,
      })
    ),
    index: PropTypes.bool.isRequired,
  }).isRequired,
};
